// FirewallWebFiltersMain.tsx
import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Globe, Loader2, RefreshCw, ChevronDown } from "lucide-react";

import { showError } from "../../../../../utils/toast";
import baseapi from "../../../../../services/baseapi";
import WebFilterProfileDetail from "./WebFilterProfileDetail";

interface ProfileOption {
    name: string;
}

export default function FirewallWebFiltersMain({ asset_id, client_id }: { asset_id: any, client_id: string }) {
    const [searchParams, setSearchParams] = useSearchParams();
    const [profiles, setProfiles] = useState<ProfileOption[]>([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);

    const dropdownRef = useRef<HTMLDivElement | null>(null);

    const activeProfile = searchParams.get("wf_profile") ?? "";

    const setActiveProfile = (name: string) => {
        setOpen(false);
        setSearchParams(prev => {
            prev.set("wf_profile", name);
            return prev;
        });
    };

    const fetchProfiles = async () => {
        setLoading(true);
        try {
            const res = await baseapi.get("/firewall_web_filter_profiles", {
                params: { fields: ["name"], query: JSON.stringify({ asset_id: asset_id, client_id: client_id }) },
            });

            if (res.data.status) {
                const list: ProfileOption[] = res.data.data ?? [];
                setProfiles(list);

                const valid = list.map(p => p.name);
                if ((!activeProfile || !valid.includes(activeProfile)) && list[0]) {
                    setSearchParams(prev => {
                        prev.set("wf_profile", list[0].name);
                        return prev;
                    }, { replace: true });
                }
            } else {
                showError("Failed to load web filter profiles");
            }
        } catch (e: any) {
            showError(e.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchProfiles();
    }, []);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target as Node)
            ) {
                setOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    return (
        <div className="flex flex-col bg-transparent text-gray-200">
            {/* Toolbar */}
            <div className="flex items-center gap-3 mb-4">
                {/* Dropdown */}
                <div className="relative" ref={dropdownRef}>
                    <button
                        onClick={() => setOpen(o => !o)}
                        disabled={loading || profiles.length === 0}
                        className="flex items-center gap-2 min-w-[220px] bg-gray-800 border border-gray-700 rounded-xl px-3 py-2 text-[11px] font-bold text-gray-200 hover:border-purple-500/50 hover:bg-gray-700/80 transition-all disabled:opacity-40 disabled:cursor-not-allowed group"
                    >
                        <Globe size={13} className="text-purple-400 shrink-0" />
                        <span className="flex-1 text-left truncate">
                            {loading ? "Loading profiles..." : activeProfile || "Select a profile"}
                        </span>
                        <ChevronDown
                            size={13}
                            className={`text-gray-200/50 transition-transform shrink-0 ${open ? "rotate-180" : ""}`}
                        />
                    </button>

                    {/* Dropdown Menu */}
                    {open && profiles.length > 0 && (
                        <div
                            className="absolute top-full left-0 mt-1.5 w-full min-w-[220px] max-h-72 overflow-y-auto overscroll-contain bg-gray-800 border border-gray-700 rounded-xl shadow-xl z-50 py-1 custom-scroll"
                            onWheel={(e) => e.stopPropagation()}
                        >
                            {profiles.map(({ name }) => {
                                const isActive = activeProfile === name;
                                return (
                                    <button
                                        key={name}
                                        onClick={() => setActiveProfile(name)}
                                        className={`w-full flex items-center gap-2 px-3 py-2 text-[11px] font-bold text-left transition-all
                                            ${isActive
                                                ? "bg-purple-500/15 text-purple-400"
                                                : "text-gray-200/70 hover:bg-gray-700/60 hover:text-gray-200"
                                            }`}
                                    >
                                        <Globe
                                            size={11}
                                            className={isActive ? "text-purple-400" : "text-gray-200/30"}
                                        />
                                        <span className="truncate">{name}</span>
                                        {isActive && (
                                            <span className="ml-auto w-1.5 h-1.5 rounded-full bg-purple-400 shrink-0" />
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Profile count badge */}
                {!loading && profiles.length > 0 && (
                    <span className="px-2 py-1 rounded-lg text-[9px] font-bold bg-purple-500/10 border border-purple-500/20 text-purple-400">
                        {profiles.length} profile{profiles.length !== 1 ? "s" : ""}
                    </span>
                )}

                {/* Refresh */}
                <button
                    onClick={fetchProfiles}
                    disabled={loading}
                    className="ml-auto p-1.5 bg-gray-800 border border-gray-700 rounded-lg text-gray-200/60 hover:text-gray-200 hover:bg-gray-700 transition-all group"
                >
                    <RefreshCw
                        size={13}
                        className={loading ? "animate-spin text-purple-400" : "group-hover:text-purple-400"}
                    />
                </button>
            </div>

            <div className="border-b border-gray-800 mb-4" />

            {/* Content */}
            {loading ? (
                <div className="flex items-center justify-center gap-2 py-20">
                    <Loader2 className="w-4 h-4 animate-spin text-purple-400" />
                    <span className="text-[10px] font-bold uppercase tracking-widest text-gray-200/40">
                        Loading profiles...
                    </span>
                </div>
            ) : activeProfile ? (
                <div className="animate-fade-in" key={activeProfile}>
                    <WebFilterProfileDetail asset_id={asset_id} client_id={client_id} profile_name={activeProfile} />
                </div>
            ) : (
                <div className="flex flex-col items-center gap-2 py-16 opacity-40">
                    <Globe size={22} className="text-gray-200/40" />
                    <span className="text-[10px] font-bold uppercase tracking-widest text-gray-200/40">
                        Select a profile from the dropdown
                    </span>
                </div>
            )}
        </div>
    );
}